/**
 * Dates a ten-year-old reads without decoding.
 *
 * The resume list used to print `session_date` exactly as the API sends it —
 * `2026-09-15`. That is the right format for a database and the wrong one for the
 * person deciding whether the half-finished thing on screen is the one they were
 * doing after lunch. "Today" and "Yesterday" answer that question outright, and
 * those two cover nearly every row that shows up here, because a session stays
 * open for hours rather than months.
 *
 * Deliberately no date library. This is one comparison and one `toLocaleDateString`.
 */

/**
 * Parse `YYYY-MM-DD` as a date in the reader's own timezone.
 *
 * `new Date("2026-09-15")` is **UTC** midnight by spec, so in New Zealand it lands on
 * the 15th at noon and in Los Angeles on the 14th at five in the afternoon — which is
 * how "Today" becomes "Yesterday" for anyone west of Greenwich. Splitting the parts
 * and handing them to the multi-argument constructor keeps it local, which is what a
 * calendar date without a time means.
 */
function localDate(iso: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return null;
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

/** Whole days from `from` to `to`, counted on the calendar rather than in hours. */
function daysBetween(from: Date, to: Date): number {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
  return Math.round((b - a) / 86_400_000);
}

/**
 * `2026-09-15` → "Today", "Yesterday", "Mon 14 Sep", or "14 Sep 2025".
 *
 * Anything it cannot parse comes back untouched, because a surprising string is still
 * more use on screen than an empty gap where the date was.
 */
export function whenText(iso: string, now: Date = new Date()): string {
  const date = localDate(iso);
  if (!date) return iso;

  const days = daysBetween(date, now);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";

  // Inside a week the weekday is the useful part — "Thursday" is how someone who
  // comes to a session on Thursdays recognises their own work.
  if (days > 1 && days < 7) {
    return date.toLocaleDateString(undefined, { weekday: "long" });
  }

  // Past that, the year only earns its place once it is not this one.
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}
